// Onboarding persistence + gating logic. Merge-writes the onboarding fields to
// users/{uid} (reusing profileService.updateUserProfile = setDoc merge), and
// answers the gating questions with lazy grandfathering (no migration script).
import { updateUserProfile } from '../../firebase/profileService';
import { ONBOARDING_ENABLED, ONBOARDING_VERSION } from './onboardingConfig';

// --- Gating / grandfathering ------------------------------------------------

// A user is "done" with onboarding if their doc predates the feature (no
// `onboarding` field → grandfathered complete) OR they finished/skipped it.
export function isOnboardingComplete(profile) {
  if (!profile) return true; // don't block while profile is unknown
  const ob = profile.onboarding;
  if (!ob) return true; // grandfathered
  return ob.status === 'completed' || ob.status === 'skipped';
}

// Route into the flow only when explicitly mid-flow. Absent/terminal ⇒ never.
export function shouldRouteToOnboarding(profile) {
  if (!ONBOARDING_ENABLED) return false;
  if (!profile || !profile.onboarding) return false;
  return profile.onboarding.status === 'in_progress';
}

// --- Writes (all merge, committed per step) ---------------------------------

async function merge(uid, patch, breadcrumb) {
  if (!uid) return;
  try {
    await updateUserProfile(uid, patch);
    if (breadcrumb) console.info(breadcrumb, { uid, ...flatten(patch) });
  } catch (err) {
    console.error('onboarding.merge failed', err, { uid, patch: safe(patch) });
    throw err;
  }
}

// Start (idempotent): stamp in_progress + startedAt if not already present.
export async function startOnboarding(uid, existing) {
  const already = existing && existing.onboarding && existing.onboarding.startedAt;
  const onboarding = {
    status: 'in_progress',
    step: existing?.onboarding?.step || 0,
    version: ONBOARDING_VERSION,
    startedAt: already || new Date(),
  };
  await merge(uid, { onboarding }, 'onboarding: start');
  return onboarding;
}

// Save progress + any collected fields for a step.
export async function saveStep(uid, step, fields = {}) {
  await merge(
    uid,
    { onboarding: { status: 'in_progress', step, version: ONBOARDING_VERSION }, ...fields },
    `onboarding: step ${step}`
  );
}

export async function completeOnboarding(uid, fields = {}) {
  await merge(
    uid,
    { onboarding: { status: 'completed', version: ONBOARDING_VERSION, completedAt: new Date() }, ...fields },
    'onboarding: complete'
  );
}

export async function skipOnboarding(uid, step) {
  await merge(
    uid,
    { onboarding: { status: 'skipped', step, version: ONBOARDING_VERSION } },
    'onboarding: skip'
  );
}

// Admin test hook: replay the flow for the current admin.
export async function resetOnboarding(uid) {
  await merge(
    uid,
    { onboarding: { status: 'in_progress', step: 0, version: ONBOARDING_VERSION, startedAt: new Date() } },
    'onboarding: reset (admin test)'
  );
}

// --- helpers ---
function flatten(patch) {
  const ob = patch.onboarding || {};
  return { status: ob.status, step: ob.step };
}
function safe(patch) {
  try { return JSON.stringify(patch).slice(0, 300); } catch (_) { return '[unserializable]'; }
}

const onboardingServiceExport = {
  isOnboardingComplete,
  shouldRouteToOnboarding,
  startOnboarding,
  saveStep,
  completeOnboarding,
  skipOnboarding,
  resetOnboarding,
};

export default onboardingServiceExport;
